import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { Router } from '@angular/router';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { Project } from '../firestore-models/project';
import { FireUser } from '../firestore-models/user';
import { ApiProject, ApiSetProjectsRequest } from '../api-client-module';

import { SessionService } from '../session.service';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { TopDialogComponent } from './top-dialog/top-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-top',
  templateUrl: './top.component.html',
  styleUrls: ['./top.component.css'],
  standalone: false,
})
export class TopComponent implements OnInit, OnDestroy {

  // プロジェクト情報
  readonly projectLink = AppConst.ROUTING_LINK_PROJECT;
  // ユーザータイプ
  readonly USER_TYPE_ADMIN: number = AppConst.MANAGE_USER_TYPE_ADMIN;
  readonly USER_TYPE_MASTER: number = AppConst.MANAGE_USER_TYPE_MASTER;
  readonly USER_TYPE_USER: number = AppConst.MANAGE_USER_TYPE_USER;

  isEdit: boolean = false;

  user!: FireUser;
  adminUser!: boolean;

  private projectsCollection!: AngularFirestoreCollection<Project>;
  projectsObservable!: Observable<Project[]>;
  projectsSubscribe!: Subscription;

  displayedColumns: string[] = ['position', 'edit', 'select', 'id', 'name', 'desc', 'button'];
  dataSource = new MatTableDataSource<Project>();
  selection = new SelectionModel<Project>(true, []);

  currentLang: string = 'ja';
  deleteMessage!: string;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private configurationService: ConfigurationService,
    private sessionService: SessionService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.translate.setDefaultLang('ja');
  }


  ngOnInit() {
    if (this.sessionService.session.isLogin) {
      this.currentLang = this.translate.currentLang;
      this.user = this.sessionService.session.user!;
      this.configurationService.setUserType(this.user.user_type);
      if (this.user.user_type == this.USER_TYPE_ADMIN || this.user.user_type == this.USER_TYPE_MASTER) {
        this.configurationService.setIsEdit(true);
      } else if (this.user.user_type == this.USER_TYPE_USER) {
        this.configurationService.setIsEdit(this.user.is_edit);
        if (this.user.exhibit_ids && this.user.exhibit_ids.length > 0) {
          this.configurationService.setExhibitIds(this.user.exhibit_ids);
        }
      } else {
        this.configurationService.setIsEdit(false);
      }
      if (this.user.user_type === this.USER_TYPE_ADMIN) {
        this.projectsCollection = this.angularFirestore.collection<Project>(AppConst.FIRESTORE_PROJECTS);
        this.projectsObservable = this.projectsCollection.valueChanges({ idField: 'id' });
        this.projectsSubscribe = this.projectsObservable.subscribe(projects => {
          this.dataSource.data = projects;
        });
        this.adminUser = true;

      } else {
        if (!!this.user.project_ids) {
          // TODO: in で取得できるのは最大10まで。11件以上ドキュメントがあると落ちるので対策を考える必要あり
          this.projectsCollection = this.angularFirestore.collection<Project>(
            AppConst.FIRESTORE_PROJECTS, ref => ref.where('project_id', 'in', this.user.project_ids));
          this.projectsObservable = this.projectsCollection.valueChanges({ idField: 'id' });
          this.projectsSubscribe = this.projectsObservable.subscribe(projects => {
            this.dataSource.data = projects;
          });
        }
      }
    }
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.projectsSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Project): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //データ管理へボタン
  selectProject(projectId: string, projectName: string): void {
    this.configurationService.setProjectName(projectName);
    this.configurationService.setProjectId(projectId);
    this.router.navigate([AppConst.ROUTING_LINK_PROJECT]);
  }

  //プロジェクト情報編集ダイアログ
  openEditDialog(project: Project): void {
    let apiProject: ApiProject = {
      project_id: project.project_id,
      project_name: project.project_name,
      project_app_names: project.project_app_names,
      project_desc: project.project_desc,
      languages: project.languages,
      maintenance_flag: project.maintenance_flag,
      enabled: project.enabled,
      facility_registration_limit: project.facility_registration_limit,
      keypad_type: project.keypad_type,
      trans_base_lang: project.trans_base_lang,
      project_base_lang: project.project_base_lang,
      use_trans_base_lang_ko: project.use_trans_base_lang_ko,
      use_trans_base_lang_yuehk: project.use_trans_base_lang_yuehk,
      use_trans_base_lang_zhcn: project.use_trans_base_lang_zhcn,
      use_trans_base_lang_zhtw: project.use_trans_base_lang_zhtw,
    }

    const dialogRef = this.dialog.open(TopDialogComponent, {
      width: '80%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, project: apiProject, adminUser: this.adminUser }
    });
  }

  //プロジェクト情報追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(TopDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, project: null, adminUser: this.adminUser }
    });
  }

  //削除ボタン
  deleteData(): void {

    // this.translate.get('登録できませんでした。').subscribe((res: string) => {
    //   const dialogRef = this.dialog.open(MessageDialogComponent, {
    //     width: '420px',
    //     height: '180px',
    //     disableClose: true,
    //     data: { message: res }
    //   });
    // });

    this.translate.get('選択されたデータを削除します。よろしいですか？').subscribe((res: string) => {
      this.deleteMessage = res;
    });

    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.deleteMessage }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedProjects: Array<ApiProject> = new Array<ApiProject>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedProject: ApiProject = {
            project_id: row.project_id,
            project_name: row.project_name,
            project_app_names: row.project_app_names,
            project_desc: row.project_desc,
            project_base_lang: row.project_base_lang,
            languages: row.languages,
            maintenance_flag: row.maintenance_flag,
            enabled: row.enabled,
            facility_registration_limit: row.facility_registration_limit,
            use_trans_base_lang_ko: row.use_trans_base_lang_ko,
            use_trans_base_lang_yuehk: row.use_trans_base_lang_yuehk,
            use_trans_base_lang_zhcn: row.use_trans_base_lang_zhcn,
            use_trans_base_lang_zhtw: row.use_trans_base_lang_zhtw,
          };
          selectedProjects.push(selectedProject);
        }
      }
      if (!!result) {
        let request: ApiSetProjectsRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          projects: selectedProjects
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setProjects(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              // this.toastr.success('削除しました。');
              this.translate.get('削除しました。').subscribe((res: string) => {
                this.toastr.success(res);
              });
            } else {
              // this.toastr.error('削除できませんでした。');
              this.translate.get('削除できませんでした。').subscribe((res: string) => {
                this.toastr.success(res);
              });
            }
          }
        ).catch(
          error => {
            console.log(error);
            // this.toastr.error('削除できませんでした。');
            this.translate.get('削除できませんでした。').subscribe((res: string) => {
              this.toastr.success(res);
            });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'id':
          return compare(a.project_id, b.project_id, isAsc);
        case 'name':
          return compare(a.project_name, b.project_name, isAsc);
        case 'desc':
          return compare(a.project_desc, b.project_desc, isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
